/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import qs from 'qs';
import useSWR from 'swr';

import request from '@/utils/request';
import type * as Type from '@/common/interface';

export const useMobileSkinMarket = (params: Type.MobileSkinListReq) => {
  const apiUrl = `/answer/api/v1/mobile-themes?${qs.stringify(params, {
    skipNulls: true,
  })}`;
  const { data, error, mutate } = useSWR<
    Type.ListResult<Type.MobileSkinItem>,
    Error
  >([apiUrl], request.instance.get);
  return {
    data,
    isLoading: !data && !error,
    error,
    mutate,
  };
};

export const useMyMobileSkins = (params: Type.MobileSkinListReq) => {
  const apiUrl = `/answer/api/v1/mobile-themes/mine?${qs.stringify(params, {
    skipNulls: true,
  })}`;
  const { data, error, mutate } = useSWR<
    Type.ListResult<Type.MobileSkinItem>,
    Error
  >([apiUrl], request.instance.get);
  return {
    data,
    isLoading: !data && !error,
    error,
    mutate,
  };
};

export const useMobileSkinDetail = (id?: string | number) => {
  const apiUrl = id
    ? `/answer/api/v1/mobile-themes/${encodeURIComponent(String(id))}`
    : null;
  const { data, error, mutate } = useSWR<Type.MobileSkinDetail, Error>(
    apiUrl ? [apiUrl] : null,
    request.instance.get,
  );
  return {
    data,
    isLoading: Boolean(apiUrl) && !data && !error,
    error,
    mutate,
  };
};

export const useMobileSkinComments = (
  id?: string | number,
  params: Type.MobileSkinCommentListReq = {
    page: 1,
    page_size: 20,
  },
) => {
  const apiUrl = id
    ? `/answer/api/v1/mobile-themes/${encodeURIComponent(String(id))}/comments?${qs.stringify(
        params,
        { skipNulls: true },
      )}`
    : null;
  const { data, error, mutate } = useSWR<
    Type.ListResult<Type.MobileSkinCommentItem>,
    Error
  >(apiUrl ? [apiUrl] : null, request.instance.get);
  return {
    data,
    isLoading: Boolean(apiUrl) && !data && !error,
    error,
    mutate,
  };
};

export const deleteMobileSkin = (id: string | number) => {
  return request.delete(
    `/answer/api/v1/mobile-themes/${encodeURIComponent(String(id))}`,
  );
};

export const toggleMobileSkinLike = (id: string | number) => {
  return request.post<Type.MobileSkinLikeResult>(
    `/answer/api/v1/mobile-themes/${encodeURIComponent(String(id))}/like`,
  );
};

export const createMobileSkinComment = (
  id: string | number,
  content: string,
) => {
  return request.post<Type.MobileSkinCommentItem>(
    `/answer/api/v1/mobile-themes/${encodeURIComponent(String(id))}/comments`,
    { content },
  );
};

export const deleteMobileSkinComment = (
  skinId: string | number,
  commentId: string | number,
) => {
  return request.delete(
    `/answer/api/v1/mobile-themes/${encodeURIComponent(String(skinId))}/comments/${encodeURIComponent(String(commentId))}`,
  );
};
